import {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Tag} from '@/components/ui/tag'
import {AccionLigadaLink} from '@/components/accion-ligada-link'
import {ConfirmDialog} from '@/components/confirm-dialog'
import {CrearMejoraDialog} from '@/components/crear-mejora-dialog'
import {RiesgosDeLaNorma} from '@/components/vinculo-norma-riesgo'
import {MARK_PRESSED} from '@/components/sgsst/item-row'
import {MicroLabel} from '@/components/period-selector'
import {useBusiness} from '@/hooks/empresa/organizacion/use-businesses'
import {
  useCrearMejoraLegal,
  useDesincorporarItem,
  usePatchLegalItem,
  type LegalItem,
  type LegalItemEstado,
} from '@/hooks/sgsst/matriz_legal/use-matriz-legal'
import {normLabel, normLabelProsa} from '@/lib/norma-label'
import {formatDate} from '@/lib/risk'
import {translateApiError} from '@/lib/errors'
import {cn} from '@/lib/utils'

const ESTADO_TONE: Record<LegalItemEstado, 'green' | 'red' | 'neutral' | 'amber'> = {
  cumple: 'green',
  no_cumple: 'red',
  no_aplica: 'neutral',
  pendiente_verificar: 'amber',
}

export function RegistroRow({
  businessId,
  item,
  readOnly,
  onEditarPropia,
}: {
  businessId: string
  item: LegalItem
  readOnly: boolean
  onEditarPropia: (item: LegalItem) => void
}) {
  const {t} = useTranslation()
  const patch = usePatchLegalItem(businessId)
  const desincorporar = useDesincorporarItem(businessId)
  const [confirming, setConfirming] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const facts = item.facts_snapshot as Record<string, unknown>
  return (
    <li
      className="-mx-4 border-b border-border px-4 py-3.5 transition-colors last:border-b-0 hover:bg-secondary/30"
      data-testid="registro-row"
      // El chip de la norma en el plan aterriza acá con `?item=<id>`.
      data-highlight-id={`item-${item.id}`}>
      <div className="flex flex-wrap items-start gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-sm text-foreground">
            <span className="font-mono text-xs text-muted-foreground">{normLabel(item)}</span> {item.titulo}
            {/* Lo declarado por la empresa se audita distinto que lo derivado. */}
            {item.es_propia ? (
              <span className="ml-2 align-middle" data-testid="registro-propia-tag">
                <Tag tone="neutral">{t('matrizLegal.propia.tag')}</Tag>
              </span>
            ) : null}
          </p>
          {item.derogado_por_norm_ref ? (
            <p className="tone-amber mt-1 inline-block rounded px-1.5 py-0.5 text-xs" data-testid="reemplazada-note">
              {t('matrizLegal.registro.reemplazada', {successor: item.derogado_por_norm_ref})}
            </p>
          ) : null}
        </div>
        <Tag tone={ESTADO_TONE[item.estado]}>{t(`matrizLegal.estado.${item.estado}`)}</Tag>
      </div>
      <details className="mt-1.5" data-testid="registro-details">
        <summary className="cursor-pointer text-xs text-primary hover:text-foreground" data-testid="procedencia-toggle">
          {t('matrizLegal.registro.detalle')}
        </summary>
        <p className="mt-1.5 text-xs text-muted-foreground">{item.articulos_clave}</p>
        {item.incorporado_at ? (
          <p className="mt-1.5 text-xs text-muted-foreground" data-testid="registro-incorporado">
            {item.incorporado_por
              ? t('matrizLegal.registro.incorporadoPor', {
                  nombre: item.incorporado_por,
                  fecha: formatDate(item.incorporado_at),
                })
              : t('matrizLegal.registro.incorporadoEl', {fecha: formatDate(item.incorporado_at)})}
          </p>
        ) : null}
        <div className="mt-2 rounded-md bg-muted/40 px-3 py-2" data-testid="procedencia-box">
          <MicroLabel>{t('matrizLegal.registro.procedencia')}</MicroLabel>
          <ul className="mt-1 space-y-1">
            {item.provenance.map((m) => (
              <li key={m.rule_key} className="text-xs text-muted-foreground">
                <span className="font-medium text-foreground">
                  {t(`matrizLegal.procedenciaSource.${m.applicability_source}`)}
                </span>{' '}
                — {m.basis_citation}
              </li>
            ))}
          </ul>
          <p className="mt-2 text-xs text-muted-foreground" data-testid="procedencia-facts">
            {t('matrizLegal.registro.hechos', {
              risk: String(facts.risk_class ?? '—'),
              workers: String(facts.worker_count ?? '—'),
              ciiu: String(facts.ciiu_code ?? '—'),
              date: String(facts.evaluated_on ?? '—'),
            })}
          </p>
        </div>
        {item.fuente_url ? (
          <a
            href={item.fuente_url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="fuente-oficial-link"
            className="mt-2 inline-block text-xs text-primary hover:underline">
            {t('matrizLegal.registro.fuenteOficial')}
          </a>
        ) : null}
      </details>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <EstadoControls businessId={businessId} item={item} patch={patch} readOnly={readOnly} />
        {item.es_propia ? (
          <Button
            size="sm"
            variant="outline"
            className="min-h-11"
            disabled={readOnly}
            onClick={() => onEditarPropia(item)}
            data-testid="editar-propia-btn">
            {t('matrizLegal.propia.editar')}
          </Button>
        ) : null}
        <Button
          size="sm"
          variant="outline"
          className="min-h-11"
          disabled={readOnly || desincorporar.isPending}
          onClick={() => setConfirming(true)}
          data-testid="desincorporar-btn">
          {t(item.es_propia ? 'matrizLegal.propia.retirar' : 'matrizLegal.registro.desincorporar')}
        </Button>
        {error ? <span className="text-xs text-destructive">{error}</span> : null}
      </div>
      <RiesgosDeLaNorma businessId={businessId} item={item} readOnly={readOnly} />
      {item.estado === 'no_cumple' ? (
        <CrearMejoraBlock businessId={businessId} item={item} readOnly={readOnly} />
      ) : null}
      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        title={t('matrizLegal.desincorporar.title', {norm: normLabel(item)})}
        description={
          item.riesgos.length > 0
            ? t('matrizLegal.desincorporar.bodyConRiesgos', {count: item.riesgos.length})
            : t('matrizLegal.desincorporar.body')
        }
        confirmLabel={t('matrizLegal.desincorporar.confirm')}
        cancelLabel={t('common.cancel')}
        pending={desincorporar.isPending}
        onConfirm={() =>
          desincorporar.mutate(item.norm_ref, {
            onSuccess: () => setConfirming(false),
            onError: (e) => {
              setError(translateApiError(e))
              setConfirming(false)
            },
          })
        }
      />
    </li>
  )
}

function CrearMejoraBlock({businessId, item, readOnly}: {businessId: string; item: LegalItem; readOnly: boolean}) {
  const {t} = useTranslation()
  const {data: business} = useBusiness(businessId)
  const crear = useCrearMejoraLegal(businessId)
  const [creando, setCreando] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const abiertas = item.acciones.filter((a) => a.estado !== 'cumplida')
  const ligada = abiertas[0] ?? item.acciones[0] ?? null
  return (
    <div className="mt-2 flex flex-wrap items-center gap-2" data-testid="crear-mejora-legal-block">
      {ligada ? (
        <AccionLigadaLink businessId={businessId} accion={ligada} testId="crear-mejora-legal-link" />
      ) : (
        <>
          <Button
            size="sm"
            variant="outline"
            className="min-h-11"
            disabled={readOnly}
            onClick={() => setCreando(true)}
            data-testid="crear-mejora-legal">
            {t('matrizLegal.registro.crearMejora')}
          </Button>
        </>
      )}
      {error ? <span className="text-xs text-destructive">{error}</span> : null}
      <CrearMejoraDialog
        businessId={businessId}
        open={creando}
        onOpenChange={setCreando}
        origen={{
          etiqueta: t('mejora.origen.norma'),
          titulo: normLabelProsa(item),
          detalle: item.titulo,
          testId: 'crear-mejora-legal-dialog',
        }}
        descripcion={item.mejora_titulo}
        responsableId={business?.legal_representative_id ?? null}
        onCrear={async (editado) => {
          setError(null)
          await crear.mutateAsync({itemId: item.id, editado}).catch((e: unknown) => {
            setError(translateApiError(e))
            throw e
          })
        }}
      />
    </div>
  )
}

function EstadoControls({
  item,
  patch,
  readOnly,
}: {
  businessId: string
  item: LegalItem
  patch: ReturnType<typeof usePatchLegalItem>
  readOnly: boolean
}) {
  const {t} = useTranslation()
  const [error, setError] = useState<string | null>(null)
  const set = (estado: LegalItemEstado, justificacion?: string) =>
    patch.mutate({itemId: item.id, estado, justificacion}, {onError: (e) => setError(translateApiError(e))})
  return (
    <>
      <div className="flex gap-0.5 rounded-lg bg-secondary p-0.5" role="group">
        {(['cumple', 'no_cumple'] as const).map((estado) => (
          <button
            key={estado}
            type="button"
            aria-pressed={item.estado === estado}
            disabled={readOnly || item.estado === estado}
            onClick={() =>
              estado === 'cumple'
                ? set('cumple')
                : set('no_cumple', item.justificacion ?? t('matrizLegal.registro.defaultJustif'))
            }
            className={cn(
              'rounded-md px-2.5 py-1.5 text-xs whitespace-nowrap transition-colors',
              item.estado === estado
                ? cn('font-semibold shadow-xs', MARK_PRESSED[estado])
                : 'text-muted-foreground hover:bg-card hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40'
            )}>
            {t(`matrizLegal.estado.${estado}`)}
          </button>
        ))}
      </div>
      {error ? <span className="text-xs text-destructive">{error}</span> : null}
    </>
  )
}
